import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Landmark, HeartPulse, ShoppingBag, Truck, GraduationCap, Building2, Leaf, Radio } from 'lucide-react';
import Eyebrow from './Eyebrow';

const ease = [0.22, 1, 0.36, 1];

const industries = [
  { icon: Landmark,     name: 'Financial Services',    subs: ['FinTech', 'InsurTech', 'WealthTech', 'Banking'],        gradient: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 100%)', light: '#dbeafe' },
  { icon: HeartPulse,   name: 'Healthcare',             subs: ['Digital Health', 'MedTech', 'Telehealth', 'Clinical'],  gradient: 'linear-gradient(135deg, #e11d48 0%, #dc2626 100%)', light: '#fee2e2' },
  { icon: ShoppingBag,  name: 'Retail & E-commerce',   subs: ['DTC Brands', 'Marketplaces', 'Commerce'],               gradient: 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)', light: '#fef3c7' },
  { icon: Truck,        name: 'Logistics & Supply',    subs: ['Last-Mile', 'Fleet', 'Warehousing'],                    gradient: 'linear-gradient(135deg, #0891b2 0%, #0284c7 100%)', light: '#e0f2fe' },
  { icon: GraduationCap,name: 'EdTech',                subs: ['LMS Platforms', 'Corporate Training', 'Assessments'],   gradient: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)', light: '#d1fae5' },
  { icon: Building2,    name: 'PropTech',              subs: ['Property Mgmt', 'Smart Buildings', 'CRE'],              gradient: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)', light: '#fef3c7' },
  { icon: Leaf,         name: 'CleanTech & Energy',    subs: ['Renewables', 'Carbon Tracking', 'Grid Mgmt'],           gradient: 'linear-gradient(135deg, #166534 0%, #15803d 100%)', light: '#dcfce7' },
  { icon: Radio,        name: 'Media & Entertainment', subs: ['Streaming', 'Content Platforms', 'Live Events'],        gradient: 'linear-gradient(135deg, #7c3aed 0%, #a21caf 100%)', light: '#fae8ff' },
];

function IndustryCard({ ind, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const Icon = ind.icon;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.05, duration: 0.5, ease }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="cursor-default h-full"
    >
      <div className="rounded-2xl overflow-hidden h-full" style={{ boxShadow: '0 4px 16px -4px rgba(0,0,0,0.10)' }}>
        {/* Gradient header */}
        <div className="h-16 flex items-center px-4 relative" style={{ background: ind.gradient }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)' }}>
            <Icon size={16} color="#fff" />
          </div>
          <div className="absolute right-3 top-2 w-10 h-10 rounded-full opacity-15" style={{ background: '#fff' }} />
        </div>
        {/* Body */}
        <div className="bg-white p-4">
          <h3 className="text-[13px] font-bold mb-2.5 leading-snug" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {ind.name}
          </h3>
          <div className="flex flex-wrap gap-1">
            {ind.subs.map(s => (
              <span key={s} className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: ind.light, color: '#374151' }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Industries() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-mesh" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto w-full">
        <div ref={ref} className="mb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
            <Eyebrow>Industries</Eyebrow>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease }}
            className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight max-w-2xl leading-[1.08]"
            style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
          >
            Sectors We Serve
          </motion.h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 items-stretch">
          {industries.map((ind, i) => <IndustryCard key={ind.name} ind={ind} index={i} />)}
        </div>
      </div>
    </section>
  );
}
